import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {fetchClientPortalDeliveriesOpen, fetchClientPortalOpenInvoices, fetchClientPortalPartners} from '../api/clientPortalApi';
import { ClientPortalLayout } from '../components/ClientPortalLayout';
import { formatCurrency, formatDate } from '../utils/formatters';
import {ClientPortalFinancials} from "../components/ClientPortalFinancials";
import {ClientPortalOpenDeliveries} from "../components/ClientPortalOpenDeliveries";
import {ClientPortalDeliveryIssues} from "../components/ClientPortalDeliveryIssues";

export function ClientPortalDashboardPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['client-portal-partners'],
    queryFn: fetchClientPortalPartners,
  });
  const invoices = useQuery({ queryKey: ['client-portal-open-invoices'], queryFn: fetchClientPortalOpenInvoices });
  const deliveries = useQuery({ queryKey: ['client-portal-open-deliveries'], queryFn: fetchClientPortalDeliveriesOpen });
  const activityLoading = isLoading || invoices.isLoading || deliveries.isLoading;
  const activityError = invoices.isError || deliveries.isError;
  const companies = (data?.results || []).map((partner) => ({
    ...partner,
    invoices: (invoices.data?.results || []).filter((item) => item.partner?.name === partner.name).length,
    deliveries: (deliveries.data?.results || []).filter((item) => item.partner?.name === partner.name).length,
  }));
  const active = companies.filter((p) => p.invoices || p.deliveries);
  const inactive = companies.filter((p) => !p.invoices && !p.deliveries);
  const companyItem = (p) => (
    <li key={p.name} className="list-group-item">
      {p.name}
      {(p.invoices > 0 || p.deliveries > 0) && <div className="small text-muted mt-1">
        {[p.invoices > 0 && `${p.invoices} open invoice${p.invoices === 1 ? '' : 's'}`,
          p.deliveries > 0 && `${p.deliveries} open deliver${p.deliveries === 1 ? 'y' : 'ies'}`].filter(Boolean).join(' · ')}
      </div>}
    </li>
  );
  return (
    <ClientPortalLayout>
      <div className="container-fluid">
        {isError && (
          <Alert variant="danger" className="m-3">
            {error.message}
          </Alert>
        )}
        <div className="row justify-content-center mt-2">
          <div className="col-2">
            <h2>Finances</h2>
          </div>
        </div>
        <div className="row">
          <div className="col-2">
            <h4>Your companies:</h4>
            <ul className="list-group">
              {activityLoading ? (
                <li className="list-group-item text-center py-3"><Spinner animation="border" size="sm" /></li>
              ) : activityError ? (
                <li className="list-group-item small text-muted">Unable to load company activity.</li>
              ) : active.length ? active.map(companyItem) : (
                <li className="list-group-item small text-muted">No companies with open invoices or deliveries.</li>
              )}
            </ul>
            {!activityLoading && !activityError && inactive.length > 0 && (
              <details className="mt-2">
                <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                  Companies with no open invoices or deliveries ({inactive.length})
                </summary>
                <ul className="list-group mt-2">{inactive.map(companyItem)}</ul>
              </details>
            )}
            <ClientPortalDeliveryIssues />
          </div>
          <div className="col-10 my-1 justify-content-center">
            <ClientPortalFinancials partners={data?.results} />
            <h2 className="mt-2">Open deliveries</h2>
            <ClientPortalOpenDeliveries/>
          </div>
        </div>
      </div>
    </ClientPortalLayout>
  );
}
